import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { BASE_URL, WS_URL } from "./apiConfig";
import { setHistoryImageData, setPromptRequestToFirebase } from "./firebaseModel";
import { mapPromptToSettings } from "../utilities";
import { initialPrompt, styleList } from "./staticDataModel";

// Define the initial state for the authentication part of the Redux store
const initialState = {
  loaderText: "",
  productsData: [],
  generatedImages: [],
  productStatus: "idle",
  imageUploadStatus: "idle",
  referImageSrc: "",
  generateStatus: "idle",
  isSidebarExpanded: true,
  promptRequest: initialPrompt,
  generateSettings: {
    productModel: "",
    promptText: "",
    imageStyle: "",
    aspectRatio: "",
  },
};

export const generateImage = createAsyncThunk("generate/handleGenerate", async (_, { rejectWithValue, dispatch, getState }) => {
  return new Promise((resolve, reject) => {
    const state = getState();
    const { promptRequest } = state.generate;
    const sendPrompt = {
      ...promptRequest,
      generationSeed: Math.floor(Math.random() * 0xffffffffffffffff),
    };
    const ws = new WebSocket(WS_URL);
    console.log("Connecting to websocket server...");

    ws.onerror = (error) => {
      console.error("Error connecting to websocket server", error);
      setTimeout(() => {
        dispatch(updateLoaderText("Error connecting to websocket server"));
        reject(rejectWithValue("Error connecting to websocket server"));
      }, 1000);
    };

    ws.onopen = () => {
      console.log("Connected to websocket server");
      console.log(`Sending payload: ${JSON.stringify(sendPrompt)}`);
      ws.send(JSON.stringify(sendPrompt));
    };

    ws.onmessage = (event) => {
      try {
        console.log(`Message received: ${event.data}`);
        const response = JSON.parse(event.data);
        const imageUrls = response.map((image) => ({ imageUrl: image }));
        setHistoryImageData(imageUrls);
        resolve(imageUrls);
      } catch (e) {
        dispatch(updateLoaderText(event.data));
      }
    };
  });
});

export const getProductData = createAsyncThunk("generate/getProductData", async (adminKey, { rejectWithValue }) => {
  const response = await fetch(BASE_URL + "/products?key=" + adminKey);
  if (!response.ok) {
    // Reject if the HTTP status is not OK
    return rejectWithValue(`HTTP error! status: ${response.status}`);
  }
  return await response.json();
});

export const uploadImage = createAsyncThunk("generate/uploadImage", async (imageFile, { rejectWithValue }) => {
  const formData = new FormData();
  formData.append("file", imageFile);
  return fetch(BASE_URL + "/upload", {
    method: "POST",
    body: formData,
  })
    .then((res) => {
      if (!res.ok) {
        // Manually throw an error if the HTTP response is not ok
        return rejectWithValue(`HTTP error! status: ${res.status}`);
      }
      return res.json();
    })
    .then((data) => {
      console.log("Image data:", data.message);
      return imageFile.name;
    })
    .catch((error) => {
      return rejectWithValue(error);
    });
});

export const uploadPromptRequest = createAsyncThunk("generate/updatePromptRequest", async (prompt, { rejectWithValue, getState }) => {
  console.log("Upload prompt request:", {
    ...prompt,
  });
  return setPromptRequestToFirebase({
    ...prompt,
  });
});

const generateSlice = createSlice({
  name: "generate",
  initialState,
  reducers: {
    initialProductState: (state) => {
      state.productsData = [];
      state.productStatus = "idle";
      state.isSidebarExpanded = true;
    },
    initialGenerateState: (state) => {
      state.loaderText = "";
      state.generatedImages = [];
      state.generateStatus = "idle";
    },
    setSidebarExpended: (state, action) => {
      state.isSidebarExpanded = action.payload;
    },
    setPromptRequest(state, action) {
      const prompt = {
        ...state.promptRequest,
        ...action.payload,
      };
      state.promptRequest = prompt;
      state.generateSettings = mapPromptToSettings(prompt, state.productsData, styleList);
      console.log("generateSettings: ", mapPromptToSettings(prompt, state.productsData, styleList));
    },
    setLocalGenerateSetting: (state, action) => {
      state.generateSettings = {
        ...state.generateSettings,
        ...action.payload,
      };
    },
    updateLoaderText: (state, action) => {
      state.loaderText = action.payload;
    },
    setReferImageSrc: (state, action) => {
      state.referImageSrc = action.payload;
    },
    deleteReferImageSrc: (state) => {
      state.referImageSrc = "";
    },
  },
  extraReducers: (builder) => {
    builder
      //getProductData
      .addCase(getProductData.pending, (state) => {
        state.productStatus = "loading";
      })
      .addCase(getProductData.fulfilled, (state, action) => {
        state.productsData = action.payload;
        state.productStatus = "succeeded";
      })
      .addCase(getProductData.rejected, (state, action) => {
        state.productStatus = "failed";
      })
      //uploadImage
      .addCase(uploadImage.pending, (state) => {
        state.imageUploadStatus = "loading";
      })
      .addCase(uploadImage.fulfilled, (state, action) => {
        state.imageUploadStatus = "succeeded";
      })
      .addCase(uploadImage.rejected, (state, action) => {
        state.imageUploadStatus = "failed";
      })
      //handleGenerate
      .addCase(generateImage.pending, (state) => {
        state.generateStatus = "loading";
      })
      .addCase(generateImage.fulfilled, (state, action) => {
        state.generatedImages = action.payload;
        state.generateStatus = "succeeded";
      })
      .addCase(generateImage.rejected, (state, action) => {
        state.generateStatus = "failed";
      });
  },
});

export const { initialProductState, initialGenerateState, setSidebarExpended, setPromptRequest, setLocalGenerateSetting, updateLoaderText, setReferImageSrc, deleteReferImageSrc } = generateSlice.actions;
export default generateSlice.reducer;
